import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, API_BASE } from "@/lib/api";

const toAbs = (url) => {
  if (!url) return url;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${API_BASE}${url.slice(4)}`;
  return url;
};

/**
 * Reusable image upload field — file picker first, no URL paste.
 * Used by Journal admin (cover image) and Settings (author portrait).
 */
export default function ImageUploadField({
  value,
  onChange,
  label = "Image",
  testIdPrefix = "img",
  aspect = "aspect-[4/3]",
  helpText = "JPG / PNG / WEBP / GIF up to 5 MB.",
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large. Limit is 5 MB.");
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const abs = toAbs(data.url);
      onChange(abs);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Upload failed."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-field`}>
      <label className="gv-label">{label}</label>
      <div className="mt-3 flex flex-col md:flex-row gap-5 items-start">
        <div className={`w-36 ${aspect} bg-[#F5F5F5] border border-[#E5E5E5] overflow-hidden flex-shrink-0`}>
          {value ? (
            <img
              src={value}
              alt=""
              className="w-full h-full object-cover"
              data-testid={`${testIdPrefix}-preview`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[10px] tracking-[0.2em] uppercase text-[#5C6670] text-center px-2">
              No image yet
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0 space-y-3">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testIdPrefix}-upload`}
            className="gv-btn-gold"
          >
            {uploading ? "Uploading…" : value ? "Replace image" : "Upload from device"}
          </button>
          <p className="text-xs text-[#5C6670]">{helpText}</p>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              data-testid={`${testIdPrefix}-clear`}
              className="text-xs tracking-[0.2em] uppercase text-[#5C6670] hover:text-[#A68545] block"
            >
              Remove
            </button>
          )}
          <input
            type="file"
            ref={fileRef}
            onChange={handleFile}
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="hidden"
            data-testid={`${testIdPrefix}-file`}
          />
        </div>
      </div>
    </div>
  );
}
